// =============================================================================
// LP Express — Quiz Funnel content model.
//
// A "Quiz Funnel" landing page is a self-contained interactive funnel:
//   intro screen  →  6 scored questions (one at a time, with progress)  →  a
//   personalized result screen recommending a product/bundle.
//
// The whole quiz is stored as ONE structured blob at
// `generatedContent.quiz` (alongside `templateType: "quiz"`). It is generated
// in a single shot by a dedicated Sim Studio workflow (SIM_LP_QUIZ_URL) and is
// edited in place through the SAME generatedContent path-based `setText` ops the
// long-form template uses — e.g. ["quiz","questions",0,"options",1,"label"].
//
// Scoring model (deliberately simple, no branching/skip logic): every option
// carries a `scores` map of bucketKey → points. Answering sums the points per
// bucket; the highest-scoring bucket selects the matching `result` entry. This
// module is pure + dependency-free so it can run identically on the client
// (interactive funnel + editor) and the server (generation/normalization).
// =============================================================================

export interface LpQuizStat {
  value: string;
  label: string;
}

export interface LpQuizIntro {
  eyebrow: string;
  headline: string;
  subheadline: string;
  // Up to 3 supporting image URLs (product / lifestyle). Optional — the
  // renderer falls back to the brand's product photos when empty.
  images: string[];
  // Exactly 3 stat cards on the intro screen.
  stats: LpQuizStat[];
  // Social-proof / rating line, e.g. "★★★★★ Loved by 12,000+ customers".
  ratingLine: string;
  ctaText: string;
}

export interface LpQuizOption {
  // Stable id, e.g. "q1o2". Used as the answer key and to compute the result.
  id: string;
  label: string;
  // bucketKey → points awarded when this option is chosen.
  scores: Record<string, number>;
}

export interface LpQuizQuestion {
  id: string; // "q1"
  prompt: string;
  options: LpQuizOption[]; // 3–4 per question
  // Editor-set: a hidden question is kept in the blob (so it can be un-hidden)
  // but is skipped by the public funnel (not shown, not scored). Mutated via the
  // shared array-item ops on the path ["quiz","questions"].
  hidden?: boolean;
}

export interface LpQuizBucket {
  key: string; // "A" | "B" | ...
  label: string; // human-readable segment/routine name
}

export interface LpQuizResult {
  bucketKey: string; // maps to LpQuizBucket.key
  headline: string;
  description: string;
  // The recommended product / bundle name (copy only — the actual buy button
  // uses the page's configured checkout URL, like every other LP Express CTA).
  recommendation: string;
  benefits: string[]; // personalized benefit bullets
  ratingLine: string;
  ctaText: string;
}

// Editable, brand-authored chrome copy for the result/ending screen: the section
// labels ("Real reviews", "Your daily routine", …) and the founder-story
// heading/body. These are template COPY — not derived product/brand data — so
// operators can reword them in the builder. Only overridden keys are stored; the
// renderer falls back to its built-in default for any key left empty. Shared
// across all result buckets (the labels are identical chrome regardless of which
// result shows).
export interface LpQuizResultLabels {
  recommendedBundle?: string;
  whyFits?: string;
  dailyRoutine?: string;
  realReviews?: string;
  realIngredients?: string;
  storyEyebrow?: string;
  storyHeading?: string;
  storyBody?: string;
  commonQuestions?: string;
}

// Per-item copy overrides for the two result-page sections that are otherwise
// rendered from derived data: the "Common questions" FAQ and the "Your daily
// routine" steps. These are the BRAND's OWN copy (not third-party social proof
// like reviews, which stay a real-review picker), so operators may reword them.
// Indexed positionally against the derived list; an empty/absent slot falls back
// to the derived default. Only overridden fields are stored.
export interface LpQuizFaqOverride {
  q?: string;
  a?: string;
}
export interface LpQuizRoutineOverride {
  label?: string;
  name?: string;
  instruction?: string;
}

export interface LpQuizContent {
  intro: LpQuizIntro;
  buckets: LpQuizBucket[];
  questions: LpQuizQuestion[];
  results: LpQuizResult[]; // one per bucket
  resultLabels: LpQuizResultLabels; // editable ending-page chrome copy
  faqOverrides: LpQuizFaqOverride[]; // per-item FAQ copy overrides (positional)
  routineOverrides: LpQuizRoutineOverride[]; // per-step routine copy overrides (positional)
}

// A map of questionId → chosen optionId.
export type LpQuizAnswers = Record<string, string>;

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
}

// Coerce a possibly-loose scores map into { [bucketKey]: number }.
function normalizeScores(v: unknown): Record<string, number> {
  const src = asObj(v);
  const out: Record<string, number> = {};
  for (const [k, val] of Object.entries(src)) {
    const n = typeof val === "number" ? val : Number(val);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

// Normalize a raw quiz payload (e.g. from the Sim workflow, which may use
// snake_case or nested strings) into the canonical LpQuizContent shape. Missing
// fields become empty strings/arrays so the renderer/editor never crash — a
// wholly-empty quiz is still structurally valid (and the caller decides whether
// empty output counts as a generation failure).
export function normalizeLpQuizContent(raw: unknown): LpQuizContent {
  const o = asObj(raw);
  const introSrc = asObj(o.intro);
  const intro: LpQuizIntro = {
    eyebrow: str(introSrc.eyebrow),
    headline: str(introSrc.headline),
    subheadline: str(introSrc.subheadline ?? introSrc.subhead),
    images: strArray(introSrc.images).slice(0, 3),
    stats: (Array.isArray(introSrc.stats) ? introSrc.stats : [])
      .map((s) => ({ value: str(asObj(s).value), label: str(asObj(s).label) }))
      .slice(0, 3),
    ratingLine: str(introSrc.ratingLine ?? introSrc.rating_line),
    ctaText: str(introSrc.ctaText ?? introSrc.cta_text) || "Start the quiz",
  };

  const buckets: LpQuizBucket[] = (Array.isArray(o.buckets) ? o.buckets : [])
    .map((b) => ({ key: str(asObj(b).key), label: str(asObj(b).label) }))
    .filter((b) => b.key);

  const questions: LpQuizQuestion[] = (Array.isArray(o.questions) ? o.questions : [])
    .map((q, qi) => {
      const qo = asObj(q);
      const qid = str(qo.id) || `q${qi + 1}`;
      const options: LpQuizOption[] = (Array.isArray(qo.options) ? qo.options : [])
        .map((opt, oi) => {
          const oo = asObj(opt);
          return {
            id: str(oo.id) || `${qid}o${oi + 1}`,
            label: str(oo.label ?? oo.text),
            scores: normalizeScores(oo.scores ?? oo.buckets),
          };
        });
      const question: LpQuizQuestion = { id: qid, prompt: str(qo.prompt ?? qo.question), options };
      if (Boolean(qo.hidden)) question.hidden = true;
      return question;
    });

  const results: LpQuizResult[] = (Array.isArray(o.results) ? o.results : [])
    .map((r) => {
      const ro = asObj(r);
      return {
        bucketKey: str(ro.bucketKey ?? ro.bucket_key ?? ro.bucket ?? ro.key),
        headline: str(ro.headline),
        description: str(ro.description),
        recommendation: str(ro.recommendation ?? ro.recommended_product),
        benefits: strArray(ro.benefits),
        ratingLine: str(ro.ratingLine ?? ro.rating_line),
        ctaText: str(ro.ctaText ?? ro.cta_text) || "Shop your routine",
      };
    });

  const rlSrc = asObj(o.resultLabels);
  const resultLabels: LpQuizResultLabels = {};
  for (const k of [
    "recommendedBundle",
    "whyFits",
    "dailyRoutine",
    "realReviews",
    "realIngredients",
    "storyEyebrow",
    "storyHeading",
    "storyBody",
    "commonQuestions",
  ] as const) {
    const v = str(rlSrc[k]);
    if (v) resultLabels[k] = v;
  }

  const faqOverrides: LpQuizFaqOverride[] = Array.isArray(o.faqOverrides)
    ? o.faqOverrides.slice(0, 20).map((it: any) => {
        const e: LpQuizFaqOverride = {};
        const q = str(it?.q);
        if (q) e.q = q;
        const a = str(it?.a);
        if (a) e.a = a;
        return e;
      })
    : [];

  const routineOverrides: LpQuizRoutineOverride[] = Array.isArray(o.routineOverrides)
    ? o.routineOverrides.slice(0, 20).map((it: any) => {
        const e: LpQuizRoutineOverride = {};
        const label = str(it?.label);
        if (label) e.label = label;
        const name = str(it?.name);
        if (name) e.name = name;
        const instruction = str(it?.instruction);
        if (instruction) e.instruction = instruction;
        return e;
      })
    : [];

  return { intro, buckets, questions, results, resultLabels, faqOverrides, routineOverrides };
}

// True when a normalized quiz carries enough structure to render a real funnel
// (a headline + at least one answerable question + at least one result). Used
// by the generator to reject an empty Sim response instead of persisting a
// blank quiz.
export function isLpQuizContentUsable(quiz: LpQuizContent | null | undefined): boolean {
  if (!quiz) return false;
  const hasIntro = !!quiz.intro?.headline;
  const hasQuestions =
    Array.isArray(quiz.questions) &&
    quiz.questions.some((q) => q.prompt && q.options?.length > 0);
  const hasResults = Array.isArray(quiz.results) && quiz.results.length > 0;
  return hasIntro && hasQuestions && hasResults;
}

// Tally per-bucket points for a set of answers.
export function tallyLpQuizScores(
  quiz: LpQuizContent,
  answers: LpQuizAnswers,
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const bucket of quiz.buckets ?? []) totals[bucket.key] = 0;
  for (const question of quiz.questions ?? []) {
    if (question.hidden) continue;
    const chosenId = answers[question.id];
    if (!chosenId) continue;
    const opt = question.options.find((o) => o.id === chosenId);
    if (!opt) continue;
    for (const [bucketKey, pts] of Object.entries(opt.scores)) {
      totals[bucketKey] = (totals[bucketKey] ?? 0) + pts;
    }
  }
  return totals;
}

// Compute the winning bucket key for a set of answers. Ties break toward the
// bucket declared earliest in `quiz.buckets` (then any bucket seen in scores),
// so the result is deterministic.
export function computeLpQuizBucketKey(
  quiz: LpQuizContent,
  answers: LpQuizAnswers,
): string | null {
  const totals = tallyLpQuizScores(quiz, answers);
  const order = (quiz.buckets ?? []).map((b) => b.key);
  for (const k of Object.keys(totals)) if (!order.includes(k)) order.push(k);
  let best: string | null = null;
  let bestScore = -Infinity;
  for (const key of order) {
    const score = totals[key] ?? 0;
    if (score > bestScore) {
      bestScore = score;
      best = key;
    }
  }
  return best;
}

// Resolve the result screen for a set of answers. Falls back to the first
// result when no bucket matches (e.g. an all-empty quiz) so the funnel always
// has something to show.
export function computeLpQuizResult(
  quiz: LpQuizContent,
  answers: LpQuizAnswers,
): { result: LpQuizResult | null; bucketKey: string | null; resultIndex: number } {
  const bucketKey = computeLpQuizBucketKey(quiz, answers);
  const results = quiz.results ?? [];
  let idx = bucketKey ? results.findIndex((r) => r.bucketKey === bucketKey) : -1;
  if (idx < 0 && results.length > 0) idx = 0;
  return {
    result: idx >= 0 ? results[idx] : null,
    bucketKey,
    resultIndex: idx,
  };
}
